import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ASSETS } from "@/lib/api";
import { MaskedHeadline } from "@/components/MaskedHeadline";

// Abstract towers rising out of the Lower Mainland at city points.
const towers = [
  { left: "10%", h: 90, w: 8, city: "Surrey", delay: 1.2 },
  { left: "13%", h: 62, w: 6, delay: 1.4 },
  { left: "16%", h: 120, w: 9, delay: 1.3 },
  { left: "19%", h: 74, w: 7, delay: 1.55 },
  { left: "44%", h: 100, w: 9, city: "Coquitlam", delay: 1.6 },
  { left: "47%", h: 70, w: 7, delay: 1.75 },
  { left: "50%", h: 132, w: 10, delay: 1.7 },
  { left: "53%", h: 84, w: 8, delay: 1.85 },
  { left: "76%", h: 84, w: 8, city: "Burnaby", delay: 1.9 },
  { left: "79%", h: 112, w: 10, delay: 2.05 },
  { left: "82%", h: 66, w: 7, delay: 2.0 },
  { left: "85%", h: 96, w: 9, delay: 2.15 },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // Subtle parallax: background moves slower + slight zoom out, headline drifts up.
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative h-screen min-h-[680px] w-full overflow-hidden grain"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
      >
        <motion.div
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.hero})` }}
        />
      </motion.div>

      {/* Cinematic overlays */}
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-navy" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/70 via-navy/20 to-transparent" />

      {/* Faint architect-blueprint grid */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "linear-gradient(to right, #A57F35 1px, transparent 1px), linear-gradient(to bottom, #A57F35 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Rising towers */}
      <div className="absolute inset-x-0 bottom-0 h-[38%] pointer-events-none">
        {towers.map((t, i) => (
          <div key={i} className="absolute bottom-0 flex flex-col items-center" style={{ left: t.left }}>
            {t.city && (
              <motion.span
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: t.delay + 1.0, duration: 0.7 }}
                className="mb-2 font-mono text-[0.6rem] uppercase tracking-[0.25em] text-brass whitespace-nowrap"
                style={{ transform: `translateY(-${t.h}px)` }}
              >
                {t.city}
              </motion.span>
            )}
            <motion.div
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ delay: t.delay, duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
              className="origin-bottom"
              style={{
                width: t.w,
                height: t.h,
                background: "linear-gradient(to top, rgba(27,42,61,0.9), #A57F35)",
                boxShadow: "0 0 32px rgba(165,127,53,0.4)",
              }}
            />
          </div>
        ))}
      </div>

      {/* Copy — signature masked line reveal */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center"
      >
        {/* Overline */}
        <div className="overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ delay: 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="overline text-brass"
          >
            RE/MAX Performance Realty · Licensed since 2009
          </motion.p>
        </div>

        <MaskedHeadline
          delay={0.35}
          className="font-heading text-white text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.98] tracking-tight mt-8 max-w-5xl"
          lines={["From raw ground", <><span className="italic text-brass">to the skyline</span>—</>, "the Lower Mainland's", "court-ordered authority."]}
        />

        <div className="overflow-hidden mt-8 max-w-2xl">
          <motion.p
            initial={{ y: "110%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-body text-white/85 text-base md:text-lg leading-relaxed"
          >
            Mike Sidhu, Personal Real Estate Corporation, guides developers, lenders,
            lawyers and homebuyers through foreclosure and court-ordered real estate across Surrey,
            Coquitlam, Burnaby and beyond.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          className="flex flex-col sm:flex-row gap-4 mt-12"
        >
          <Link
            to="/listings"
            data-testid="hero-cta-listings"
            className="group bg-brass text-white px-8 py-4 flex items-center justify-center gap-2 hover:bg-white hover:text-navy transition-colors duration-300 font-body text-sm tracking-wide"
          >
            View Featured Listings
            <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <a
            href="#speak-with-mike"
            data-testid="hero-cta-speak"
            className="border border-white/40 text-white px-8 py-4 flex items-center justify-center hover:bg-white hover:text-navy transition-colors duration-300 font-body text-sm tracking-wide"
          >
            Speak With Mike
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.3, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
      >
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.35em] text-white/50">Scroll</span>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="w-px h-8 bg-brass/60"
        />
      </motion.div>
    </section>
  );
};
